import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  FaGithub,
  FaLinkedin,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaUniversity,
  FaGraduationCap,
  FaCertificate,
  FaLanguage,
} from "react-icons/fa";
import SectionHeader from "./SectionHeader";
import { fadeUpItem, staggerContainer, viewportOnce } from "../motionPresets";

const About = () => {
  const reduced = useReducedMotion();
  const skillChips = [
    "Node.js",
    "React",
    "Next.js",
    "GraphQL",
    "Spring Boot",
    "TypeScript",
    "Redux",
    "Tailwind",
    "MySQL",
    "MongoDB",
  ];

  const card =
    "rounded-2xl border border-white/[0.08] bg-slate-900/35 backdrop-blur-sm p-6 md:p-7 shadow-lg shadow-black/20 ring-1 ring-white/[0.04]";

  return (
    <div
      name="about"
      className="w-full border-t border-white/[0.06] bg-[#030712] text-white pt-20 pb-20 px-4 sm:px-6"
    >
      <div className="max-w-6xl mx-auto">
        <SectionHeader
          eyebrow="Profile"
          title="About me"
          subtitle="Full stack developer · BSc (Hons) Computer Science · Kandy, Sri Lanka"
        />

        <motion.div
          className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5"
          variants={staggerContainer}
          initial={reduced ? false : "hidden"}
          animate={reduced ? "visible" : undefined}
          whileInView={reduced ? undefined : "visible"}
          viewport={viewportOnce}
        >
          <motion.div
            variants={fadeUpItem}
            className={`md:col-span-8 ${card}`}
          >
            <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-500 mb-3">
              Summary
            </h3>
            <p className="text-slate-300 leading-relaxed text-[15px] md:text-base">
              Motivated software engineer with professional experience building scalable web applications
              across the{" "}
              <span className="text-slate-100">
                Node.js, React, Next.js, GraphQL, and Spring Boot
              </span>{" "}
              ecosystems. I have delivered production systems including ERP, POS, HRM, and LMS platforms,
              with a focus on clean architecture, performance, and collaboration.
            </p>
            <motion.div
              className="flex flex-wrap gap-2 mt-6"
              variants={staggerContainer}
              initial={reduced ? false : "hidden"}
              animate={reduced ? "visible" : undefined}
              whileInView={reduced ? undefined : "visible"}
              viewport={viewportOnce}
            >
              {skillChips.map((s) => (
                <motion.span
                  key={s}
                  variants={fadeUpItem}
                  className="text-xs font-medium px-3 py-1.5 rounded-lg bg-sky-500/10 text-sky-200/90 border border-sky-500/20"
                >
                  {s}
                </motion.span>
              ))}
            </motion.div>
            <p className="text-slate-500 text-sm mt-5">
              Detailed stack with icons is in the <span className="text-slate-400">Skills</span> section.
            </p>
          </motion.div>

          <motion.div
            variants={fadeUpItem}
            className={`md:col-span-4 ${card} bg-gradient-to-b from-slate-900/50 to-slate-950/50 flex flex-col justify-between`}
          >
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-500 mb-4">
                Contact
              </h3>
              <ul className="space-y-3.5 text-slate-300 text-sm">
                <li className="flex items-center gap-3">
                  <FaPhone className="text-sky-500 shrink-0 text-xs" />
                  <a href="tel:+94765232442" className="hover:text-sky-300 transition-colors font-medium">
                    (+94) 76 523 2442
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <FaEnvelope className="text-sky-500 shrink-0 text-xs" />
                  <a
                    href="mailto:mdnsmi2442@gmail.com"
                    className="hover:text-sky-300 transition-colors break-all"
                  >
                    mdnsmi2442@gmail.com
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <FaMapMarkerAlt className="text-sky-500 shrink-0 text-xs" />
                  <span>Kandy, Sri Lanka</span>
                </li>
              </ul>
            </div>
            <div className="mt-8 pt-6 border-t border-white/[0.06]">
              <p className="text-[11px] text-slate-500 uppercase tracking-[0.15em] mb-3 font-semibold">
                Links
              </p>
              <div className="flex flex-wrap gap-2">
                <a
                  href="https://github.com/m-nasmi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2.5 rounded-lg bg-white/[0.05] hover:bg-sky-500/15 text-slate-400 hover:text-sky-300 transition-colors ring-1 ring-white/[0.06]"
                  aria-label="GitHub"
                >
                  <FaGithub size={20} />
                </a>
                <a
                  href="https://www.linkedin.com/in/muhammed-nasmi-088a6628b/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2.5 rounded-lg bg-white/[0.05] hover:bg-sky-500/15 text-slate-400 hover:text-sky-300 transition-colors ring-1 ring-white/[0.06]"
                  aria-label="LinkedIn"
                >
                  <FaLinkedin size={20} />
                </a>
                <a
                  href="https://muhammednasmi.netlify.app/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-medium px-3 py-2 rounded-lg border border-white/[0.1] hover:border-sky-500/35 text-slate-300 hover:text-white transition-colors"
                >
                  Live portfolio
                </a>
              </div>
            </div>
          </motion.div>

          <motion.div variants={fadeUpItem} className={`md:col-span-6 ${card}`}>
            <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-500 mb-4 flex items-center gap-2">
              <FaUniversity className="text-sm opacity-80" />
              Education
            </h3>
            <ul className="space-y-5">
              <li className="flex gap-3">
                <FaGraduationCap className="text-sky-500 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-white text-[15px]">BSc (Hons) Computer Science</p>
                  <p className="text-slate-400 text-sm mt-0.5">University of Bedfordshire (UK) | SLIIT</p>
                  <p className="text-slate-500 text-xs mt-1.5 font-medium">2024 – 2025</p>
                </div>
              </li>
              <li className="flex gap-3">
                <FaGraduationCap className="text-sky-500 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-white text-[15px]">Higher Diploma in IT</p>
                  <p className="text-slate-400 text-sm mt-0.5">SLIIT City Uni</p>
                  <p className="text-slate-500 text-xs mt-1.5 font-medium">2022 – 2024</p>
                </div>
              </li>
            </ul>
          </motion.div>

          <motion.div variants={fadeUpItem} className={`md:col-span-6 ${card}`}>
            <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-500 mb-4 flex items-center gap-2">
              <FaCertificate className="text-sm opacity-80" />
              Certifications
            </h3>
            <ul className="text-slate-400 text-sm space-y-2.5 mb-8">
              <li className="flex gap-2">
                <span className="text-sky-500/80 mt-1.5 text-[6px]">●</span>
                Applied IT (SLT)
              </li>
              <li className="flex gap-2">
                <span className="text-sky-500/80 mt-1.5 text-[6px]">●</span>
                Web Design
              </li>
              <li className="flex gap-2">
                <span className="text-sky-500/80 mt-1.5 text-[6px]">●</span>
                Computerized Accounting &amp; Business
              </li>
              <li className="flex gap-2">
                <span className="text-sky-500/80 mt-1.5 text-[6px]">●</span>
                Diploma in English &amp; Graphic Design
              </li>
            </ul>
            <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-500 mb-3 flex items-center gap-2">
              <FaLanguage className="text-sm opacity-80" />
              Languages
            </h3>
            <p className="text-slate-300 text-sm font-medium">English · Sinhala · Tamil</p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default About;
